import DataGrid from "../../components/DataGrid";
import { useCallback, useEffect, useMemo } from "react";
import { transportOrdersConfig as config } from "./transportOrdersConfig.tsx";
import TableTitleRow from "../../components/TableTitleRow";
import useVisibleColumns from "../../hooks/useVisibleColumns";
import { api } from "../../api";
import { TransportOrders } from "../../types/transportOrders.ts";
import { deliveryStatusOptions } from "../../helpers/deliveryStatuses.ts";
import { useDebouncedValue } from "../../hooks/useDebouncedValue.tsx";
import { transportOrdersSearchFieldMap } from "../../helpers/searchFieldMap.ts";
import { transportOrdersSortFieldMap } from "../../helpers/sortFieldMap.ts";
import { transportOrderFromExternalList } from "../../hooks/useSetDataByView/convert.ts";
import { ViewProvider } from "../../contexts/ViewContext.tsx";
import { TableDialogs } from "../../components/TableDialogs.tsx";
import { getIsNationalFromUrl } from "../../helpers/isNational.ts";
import { extractApiError } from "../../api/errors.ts";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState.ts";
import { useSearchParams } from "react-router";
import { TRANSPORT_ORDERS_DEFAULTS } from "../../helpers/defaultUrl.ts";
import { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { ConfirmDialog } from "primereact/confirmdialog";
import { getVisibleSearchFields } from "../../helpers/getVisibleSearchFields";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { TransportOrdersDropdowns } from "../../types";
import {
  cityCountryPairLabel,
  cityCountryPairValue,
  parseCityCountryPairValue,
} from "../../helpers/cityCountryPair.ts";

const columns = config.columns;

export default function TransportOrdersView() {
  const {
    filters,
    loadingFrom,
    loadingTo,
    unloadingFrom,
    unloadingTo,
    globalFilterValue,
    setGlobalFilterValue,
    showCompletedRows,
  } = useUrlState();

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: TRANSPORT_ORDERS_DEFAULTS.page,
      limit: TRANSPORT_ORDERS_DEFAULTS.limit,
      sort: TRANSPORT_ORDERS_DEFAULTS.sort,
      order: TRANSPORT_ORDERS_DEFAULTS.order,
    });

  const [searchParams] = useSearchParams();

  const [visibleColumns, setVisibleColumns] = useVisibleColumns(columns);
  const debouncedSearchValue = useDebouncedValue(globalFilterValue, 300);

  const showToast = useShowToast();

  // search
  const visibleSearchFields = useMemo(
    () => getVisibleSearchFields(visibleColumns, transportOrdersSearchFieldMap),
    [visibleColumns],
  );

  //sorting
  const orderByField = useMemo(() => {
    const mapped = transportOrdersSortFieldMap[sort];
    const base = mapped ?? TRANSPORT_ORDERS_DEFAULTS.sort;
    const direction = order === -1 ? "-" : "";
    return `${direction}${base}`;
  }, [sort, order]);

  const filteredLoadingCityAndCountry = useMemo(() => {
    if (!filters.loadingCity) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.loadingCity);
  }, [filters.loadingCity]);

  const filteredUnloadingCityAndCountry = useMemo(() => {
    if (!filters.unloadingCity) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.unloadingCity);
  }, [filters.unloadingCity]);

  const {
    data: rawData,
    isLoading,
    error,
  } = api.endpoints.listTransportOrdersApiTransportOrdersGet.useQuery({
    orderBy: `${orderByField},-created_at`, // If two have same unloading date, let's still have some predictable order for them
    limit: limit,
    offset: page * limit,
    search: debouncedSearchValue || undefined,
    searchFields: visibleSearchFields,
    // filters
    status: filters.deliveryStatus,
    productId: filters.product,
    supplierId: filters.seller,
    buyerId: filters.buyer,
    transporterId: filters.carrier,
    driverId: filters.driver,
    driverManagerId: filters.driverManager,
    isNational: getIsNationalFromUrl(searchParams),
    entryStatus: !showCompletedRows ? "default,pending" : undefined,
    loadingDateFrom: loadingFrom
      ? loadingFrom.toISOString().split("T")[0]
      : undefined,
    loadingDateTo: loadingTo
      ? loadingTo.toISOString().split("T")[0]
      : undefined,
    unloadingDateFrom: unloadingFrom
      ? unloadingFrom.toISOString().split("T")[0]
      : undefined,
    unloadingDateTo: unloadingTo
      ? unloadingTo.toISOString().split("T")[0]
      : undefined,
    loadingCity: filteredLoadingCityAndCountry?.city,
    loadingCountry: filteredLoadingCityAndCountry?.country,
    unloadingCity: filteredUnloadingCityAndCountry?.city,
    unloadingCountry: filteredUnloadingCityAndCountry?.country,
  });

  useEffect(() => {
    if (error) {
      showToast({
        severity: "error",
        summary: "Błąd ładowania danych",
        detail: extractApiError(error),
      });
    }
  }, [error, showToast]);

  const data: TransportOrders[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(transportOrderFromExternalList);
  }, [rawData]);

  const { data: products } = api.endpoints.listProductsApiProductsGet.useQuery({
    limit: 10000,
    orderBy: "name",
  });
  const { data: suppliers } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      limit: 10000,
      isSupplier: true,
      orderBy: "name",
    });
  const { data: buyers } = api.endpoints.listCompaniesApiCompaniesGet.useQuery({
    limit: 10000,
    isBuyer: true,
    orderBy: "name",
  });
  const { data: transporters } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      limit: 10000,
      isTransporter: true,
      orderBy: "name",
    });
  const { data: drivers } = api.endpoints.listDriversApiDriversGet.useQuery({
    limit: 10000,
    orderBy: "last_name",
  });
  const { data: locations } =
    api.endpoints.listLocationsApiLocationsGet.useQuery({
      limit: 10000,
      orderBy: "address",
    });
  const { data: salesContracts } =
    api.endpoints.listContractsApiContractsGet.useQuery({
      limit: 10000,
      orderBy: "contract_number",
      contractType: "sales",
    });
  const { data: purchaseContracts } =
    api.endpoints.listContractsApiContractsGet.useQuery({
      limit: 10000,
      orderBy: "contract_number",
      contractType: "purchase",
    });
  const { data: cities } =
    api.endpoints.listCitiesApiLocationsCitiesGet.useQuery({
      limit: 10000,
      orderBy: "city",
    });
  const { data: organizationalUsers } =
    api.endpoints.getOrganizationalUsersApiOrganizationalUsersGet.useQuery({
      limit: 10000,
    });

  const dropdownConfigs = useMemo<TransportOrdersDropdowns>(() => {
    return {
      buyers:
        buyers?.items?.map((c) => ({ label: c.name, value: c.id, data: c })) ??
        [],
      suppliers:
        suppliers?.items?.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        })) ?? [],
      transporters:
        transporters?.items?.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        })) ?? [],
      products:
        products?.items?.map((p) => ({
          label: p.name,
          value: p.id,
          data: p,
        })) ?? [],
      locations:
        locations?.items.map((location) => ({
          label: `${location.country}, ${location.city}, ${location.address}`,
          value: location.id,
        })) ?? [],
      drivers:
        drivers?.items.map((driver) => ({
          label: `${driver.first_name} ${driver.last_name}`,
          value: driver.id,
          data: driver,
        })) ?? [],
      salesContracts:
        salesContracts?.items.map((contract) => ({
          label: contract.contract_number,
          value: contract.id,
          data: contract,
        })) ?? [],
      purchaseContracts:
        purchaseContracts?.items.map((contract) => ({
          label: contract.contract_number,
          value: contract.id,
          data: contract,
        })) ?? [],
      deliveryStatus: deliveryStatusOptions,
      cities:
        cities?.items.map((city) => ({
          label: cityCountryPairLabel(city),
          value: cityCountryPairValue(city),
          data: city,
        })) ?? [],
      organizationalUsers:
        organizationalUsers?.items?.map((user) => ({
          label: user.email,
          value: user.id,
        })) ?? [],
    };
  }, [
    buyers,
    suppliers,
    transporters,
    products,
    drivers,
    locations,
    salesContracts,
    purchaseContracts,
    cities,
    organizationalUsers,
  ]);

  const handleSortChange = useCallback(
    (e: DataTableSortEvent) => onSortChange(e.sortField, e.sortOrder ?? 1),
    [onSortChange],
  );
  const handlePageChange = useCallback(
    (e: DataTablePageEvent) => onPageChange(e.page ?? 0, e.rows ?? limit),
    [onPageChange, limit],
  );

  return (
    <ViewProvider view={config.view}>
      <TableDialogs />
      <ConfirmDialog />
      <TableTitleRow<TransportOrdersDropdowns, "transportOrders">
        visibleColumns={visibleColumns}
        columns={columns}
        setVisibleColumns={setVisibleColumns}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        config={config}
        dropdownConfigs={dropdownConfigs}
      />
      <DataGrid<TransportOrdersDropdowns, "transportOrders">
        data={data}
        page={page}
        onPage={handlePageChange}
        rowsPerPage={limit}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={handleSortChange}
        visibleColumns={visibleColumns}
        config={config}
        dropdownConfigs={dropdownConfigs}
        globalFilterValue={globalFilterValue}
        isLoading={isLoading}
      ></DataGrid>
    </ViewProvider>
  );
}
